import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, BookOpen } from "lucide-react";
import { getLang } from "../i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

const T = {
  en: {
    title: "The Master Monograph",
    desc:
      "Share your email and the complete Nonlocal Field Harmonization monograph will be sent to your inbox — a quiet companion for your own contemplative practice. It is offered freely, with warmth.",
    emailLabel: "Email",
    download: "Email me the Monograph",
    note: "Your email rests with us in confidence and is never shared. The monograph is delivered only by email.",
    notesLabel: "I would like to receive occasional Nonlocalfield Notes by email.",
    notesPending: "The Master Monograph is on its way to your inbox. Please also tap the link in our confirmation email to receive Nonlocalfield Notes.",
    notesAlready: "The Master Monograph is on its way to your inbox — and you already receive our Nonlocalfield Notes. Thank you.",
    invalid: "Please enter a valid email address.",
    ok: "Thank you. The Master Monograph is on its way to your inbox.",
    error: "Something interrupted the request. Please try again in a moment.",
  },
  de: {
    title: "Die Master-Monographie",
    desc:
      "Teile deine E-Mail-Adresse und die vollständige Monographie der Nichtlokalen Feldharmonisierung wird dir per E-Mail zugesandt — ein stiller Begleiter für deine eigene kontemplative Praxis. Sie wird frei und mit Wärme angeboten.",
    emailLabel: "E-Mail",
    download: "Monographie per E-Mail senden",
    note: "Deine E-Mail-Adresse ruht vertraulich bei uns und wird niemals weitergegeben. Die Monographie wird ausschließlich per E-Mail zugestellt.",
    notesLabel: "Ich möchte gelegentliche Nonlocalfield Notes per E-Mail erhalten.",
    notesPending: "Die Master-Monographie ist auf dem Weg in dein Postfach. Bitte tippe auch auf den Link in unserer Bestätigungs-E-Mail, um die Nonlocalfield Notes zu erhalten.",
    notesAlready: "Die Master-Monographie ist auf dem Weg in dein Postfach — und du erhältst unsere Nonlocalfield Notes bereits. Danke.",
    invalid: "Bitte gib eine gültige E-Mail-Adresse ein.",
    ok: "Danke. Die Master-Monographie ist auf dem Weg in dein Postfach.",
    error: "Etwas hat die Anfrage unterbrochen. Bitte versuche es gleich noch einmal.",
  },
};

const MonographModal = ({ children }) => {
  const location = useLocation();
  const lang = getLang(location.pathname);
  const t = T[lang];
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribeNotes, setSubscribeNotes] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    if (!isEmail(email)) {
      toast.error(t.invalid);
      return;
    }
    setBusy(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const { data } = await axios.post(`${API}/monograph/subscribe`, {
        email: email.trim(),
        subscribeNotes,
        lang,
        origin: window.location.origin,
      });
      if (subscribeNotes) {
        toast.success(data?.notes_status === "already" ? t.notesAlready : t.notesPending);
      } else {
        toast.success(t.ok);
      }
      setOpen(false);
      setEmail("");
      setSubscribeNotes(false);
    } catch (err) {
      toast.error(t.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="monograph-modal">
          <DialogHeader>
            <div className="monograph-mark" aria-hidden="true">
              <BookOpen size={20} />
            </div>
            <DialogTitle className="monograph-title">{t.title}</DialogTitle>
            <DialogDescription className="monograph-desc">
              {t.desc}
            </DialogDescription>
          </DialogHeader>

          <div className="monograph-body">
            <label className="field-label" htmlFor="mono-email">{t.emailLabel}</label>
            <Input
              id="mono-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />

            <label className="monograph-notes" data-testid="monograph-notes-checkbox">
              <Checkbox
                checked={subscribeNotes}
                onCheckedChange={(v) => setSubscribeNotes(v === true)}
                className="monograph-notes-box"
              />
              <span className="monograph-notes-text">{t.notesLabel}</span>
            </label>

            <button type="button" className="btn-gold w-full monograph-submit" onClick={handleSubmit} disabled={busy}>
              {busy ? <Loader2 size={16} className="spin" /> : <Mail size={16} />}
              {t.download}
            </button>
            <p className="monograph-note">
              {t.note}
            </p>
          </div>
        </DialogContent>
    </Dialog>
  );
};

export default MonographModal;
